import React, { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { profiles } from '@/data/profileData';
import { generateCoachingIntelligence } from '@/components/athlete-intelligence/coachingIntelligenceData';
import { PlatformFooter } from '@/components/common/PageElements';
import { FeedbackWidget } from '@/components/common/FeedbackWidget';
import { ProfileSelector, formatCurrency } from '@/components/coaching-intelligence/IntelligenceUIComponents';
import { ProfilePhoto } from '@/components/coaching-intelligence/ProfilePhoto';
import { ExecutiveSummarySection } from '@/components/coaching-intelligence/ExecutiveSummarySection';
import { BrandScoreSection } from '@/components/coaching-intelligence/BrandScoreSection';
import { DigitalMediaSection } from '@/components/coaching-intelligence/DigitalMediaSection';
import { TeamBrandImpactSection } from '@/components/coaching-intelligence/TeamBrandImpactSection';
import { TournamentSimulationSection } from '@/components/coaching-intelligence/TournamentSimulationSection';
import { RiskVolatilitySection } from '@/components/coaching-intelligence/RiskVolatilitySection';
import { CommercialPositioningSection } from '@/components/coaching-intelligence/CommercialPositioningSection';
import { HistoricalTimelineSection } from '@/components/coaching-intelligence/HistoricalTimelineSection';
import { BenchmarkingSection } from '@/components/coaching-intelligence/BenchmarkingSection';
import { generatePDFReport } from '@/services/pdfExportService';
import { useLanguage } from '@/i18n/LanguageContext';
import { trackEvent } from '@/hooks/useAnalytics';
import { FileDown, RefreshCw, Lock, MapPin, Flag, Loader2 } from 'lucide-react';

export default function CoachingIntelligenceDashboard() {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const [reportState, setReportState] = useState('idle');
  const activeProfile = profileId && profiles[profileId] ? profileId : 'pochettino';
  const currentProfile = profiles[activeProfile];
  const isPerez = activeProfile === 'perez';
  
  const intelligenceData = useMemo(() => {
    return generateCoachingIntelligence(activeProfile, currentProfile?.metrics);
  }, [activeProfile, currentProfile?.metrics]);

  const handleProfileChange = (newProfileId) => {
    navigate(`/athlete/${newProfileId}`);
  };

  const handleDownloadReport = async () => {
    setReportState('loading');
    trackEvent('brand_intelligence_report_download', { profile: 'perez', lang });
    try {
      await generatePDFReport(null, 'brand-intelligence', {
        lang,
        profile: currentProfile,
        intelligenceData: intelligenceData,
      });
      setReportState('idle');
    } catch {
      setReportState('error');
      setTimeout(() => setReportState('idle'), 4000);
    }
  };

  const { meta, executiveSummary } = intelligenceData;

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="coaching-intelligence-dashboard">
      <Header />
      
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-6">
        {/* Top Bar */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="text-muted-foreground">
              <Lock className="w-3 h-3 mr-1" />
              {meta.confidentiality}
            </Badge>
          </div>
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span>Last updated: {new Date(meta.lastUpdated).toLocaleDateString()}</span>
            <button className="flex items-center gap-1 hover:text-foreground transition-colors">
              <RefreshCw className="w-3 h-3" />
              Refresh
            </button>
            <button className="flex items-center gap-1 hover:text-foreground transition-colors" data-testid="export-btn">
              <FileDown className="w-3 h-3" />
              Export
            </button>
          </div>
        </div>

        {/* Profile Selector */}
        <ProfileSelector activeProfile={activeProfile} profiles={profiles} onProfileChange={handleProfileChange} />

        {/* Profile Header - Executive Identity Card */}
        <Card className="mb-6 bg-gradient-to-r from-primary/5 via-transparent to-transparent border-primary/20 overflow-hidden">
          <CardContent className="p-0">
            {/* Mobile: Centered layout */}
            <div className="flex flex-col items-center text-center p-6 md:hidden">
              <ProfilePhoto 
                src={currentProfile?.photo}
                alt={currentProfile?.name}
                initials={currentProfile?.initials}
                size={100}
                federationAccent={true}
                className="mb-4"
              />
              <div>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <h1 className="text-xl font-bold text-foreground">{currentProfile?.name}</h1>
                  <Badge className="bg-stable/20 text-stable border-stable/30 text-xs">{meta.dataIntegrity}</Badge>
                </div>
                <p className="text-sm text-muted-foreground mb-1">{currentProfile?.role}</p>
                <p className="text-sm text-muted-foreground mb-3 flex items-center justify-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {currentProfile?.organization || currentProfile?.team}
                </p>
                <div className="flex flex-col gap-2 text-sm">
                  <div className="px-3 py-2 rounded-lg bg-muted/30">
                    <span className="text-muted-foreground">Brand Value: </span>
                    <span className="font-semibold text-foreground">
                      {formatCurrency(executiveSummary.valueRange.min)} – {formatCurrency(executiveSummary.valueRange.max)}
                    </span>
                  </div>
                  <div className="flex items-center justify-center gap-3">
                    <Badge variant="outline" className="text-xs">
                      Score: {executiveSummary.strategicInfluenceScore}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {executiveSummary.institutionalStabilityRating} Rated
                    </Badge>
                  </div>
                  {isPerez && (
                    <Button
                      onClick={handleDownloadReport}
                      disabled={reportState === 'loading'}
                      className="bg-primary hover:bg-primary/90 h-9 px-4 text-xs font-semibold rounded-lg w-full mt-3"
                      data-testid="download-brand-report-mobile"
                    >
                      {reportState === 'loading' ? (
                        <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />{lang === 'es' ? 'Generando\u2026' : 'Generating\u2026'}</>
                      ) : (
                        <><FileDown className="w-3.5 h-3.5 mr-1.5" />{lang === 'es' ? 'Descargar informe' : 'Download Report'}</>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Tablet & Desktop: Side-by-side layout */}
            <div className="hidden md:flex items-start gap-6 p-6 lg:p-8">
              {/* Profile Photo */}
              <ProfilePhoto 
                src={currentProfile?.photo}
                alt={currentProfile?.name}
                initials={currentProfile?.initials}
                size={110}
                federationAccent={true}
              />

              {/* Profile Info */}
              <div className="flex-1 min-w-0">
                {/* Name Row */}
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-2xl lg:text-3xl font-bold text-foreground truncate">{currentProfile?.name}</h1>
                  <Badge className="bg-stable/20 text-stable border-stable/30 flex-shrink-0">{meta.dataIntegrity}</Badge>
                </div>

                {/* Role & Organization */}
                <div className="flex items-center gap-4 text-muted-foreground mb-4">
                  <span className="font-medium text-foreground">{currentProfile?.role}</span>
                  <span className="text-border">|</span>
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" />
                    {currentProfile?.organization || currentProfile?.team}
                  </span>
                  {currentProfile?.nationality && (
                    <>
                      <span className="text-border">|</span>
                      <span className="flex items-center gap-1">
                        <Flag className="w-3.5 h-3.5" />
                        {currentProfile?.nationality}
                      </span>
                    </>
                  )}
                </div>

                {/* Key Metrics Row */}
                <div className="flex flex-wrap items-center gap-4 lg:gap-6">
                  <div className="px-4 py-2 rounded-lg bg-muted/30 border border-border/50">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-0.5">Brand Value Range</p>
                    <p className="text-lg font-bold text-foreground">
                      {formatCurrency(executiveSummary.valueRange.min)} – {formatCurrency(executiveSummary.valueRange.max)}
                    </p>
                  </div>
                  <div className="px-4 py-2 rounded-lg bg-muted/30 border border-border/50">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-0.5">Strategic Score</p>
                    <p className="text-lg font-bold text-primary">{executiveSummary.strategicInfluenceScore}</p>
                  </div>
                  <div className="px-4 py-2 rounded-lg bg-muted/30 border border-border/50">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-0.5">Stability Rating</p>
                    <p className="text-lg font-bold text-foreground">{executiveSummary.institutionalStabilityRating}</p>
                  </div>
                  <Badge className="bg-primary/10 text-primary border-primary/20 px-3 py-1.5">
                    {currentProfile?.role === 'Head Coach' ? 'Executive Leadership' : 'Supporting Staff'}
                  </Badge>
                  {isPerez && (
                    <Button
                      onClick={handleDownloadReport}
                      disabled={reportState === 'loading'}
                      className="bg-primary hover:bg-primary/90 h-9 px-4 text-xs font-semibold rounded-lg ml-auto"
                      data-testid="download-brand-report"
                    >
                      {reportState === 'loading' ? (
                        <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />{lang === 'es' ? 'Generando informe\u2026' : 'Generating report\u2026'}</>
                      ) : (
                        <><FileDown className="w-3.5 h-3.5 mr-1.5" />{lang === 'es' ? 'Descargar informe de marca' : 'Download Brand Intelligence Report'}</>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Report error state */}
        {reportState === 'error' && (
          <div className="mb-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-sm text-destructive" data-testid="report-error">
            {lang === 'es' ? 'No se ha podido generar el informe. Inténtalo de nuevo.' : 'The report could not be generated. Please try again.'}
          </div>
        )}

        {/* All 9 Intelligence Sections — ordered by executive priority */}
        <CommercialPositioningSection data={intelligenceData} />
        <TeamBrandImpactSection data={intelligenceData} />
        <RiskVolatilitySection data={intelligenceData} />
        <ExecutiveSummarySection data={intelligenceData} />
        <BrandScoreSection data={intelligenceData} />
        <DigitalMediaSection data={intelligenceData} />
        <TournamentSimulationSection data={intelligenceData} />
        <BenchmarkingSection data={intelligenceData} activeProfile={activeProfile} />
        <HistoricalTimelineSection data={intelligenceData} />
      </main>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <FeedbackWidget route={`/athlete/${activeProfile}`} />
      </div>
      <PlatformFooter />
    </div>
  );
}
